import { useState, useEffect } from 'react';
import { useIsMobile } from '@/hooks/use-mobile';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Monitor, Smartphone, X } from 'lucide-react';

const STORAGE_KEY = 'hide-mobile-warning';

export function MobileWarningDialog() {
  const isMobile = useIsMobile();
  const [open, setOpen] = useState(false);
  const [shouldShow, setShouldShow] = useState(true);

  useEffect(() => {
    // Verificar se usuário já clicou em "Não mostrar novamente"
    const hideWarning = localStorage.getItem(STORAGE_KEY);
    
    if (hideWarning === 'true') {
      setShouldShow(false);
      return;
    }

    // Se for mobile/tablet, mostrar o modal após 500ms
    if (isMobile) {
      const timer = setTimeout(() => {
        setOpen(true);
      }, 500);

      return () => clearTimeout(timer);
    }
  }, [isMobile]);

  const handleDontShowAgain = () => {
    localStorage.setItem(STORAGE_KEY, 'true');
    setShouldShow(false);
    setOpen(false);
  };

  const handleClose = () => {
    setOpen(false);
    // Não salva no localStorage, então mostrará novamente na próxima visita
  };

  if (!shouldShow || !isMobile) return null;

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogContent 
        className="sm:max-w-[500px] max-w-[90vw] border-none shadow-2xl"
        style={{
          background: 'rgba(17, 17, 17, 0.95)',
          backdropFilter: 'blur(20px)',
          WebkitBackdropFilter: 'blur(20px)',
        }}
      >
        {/* Botão X customizado no canto superior direito */}
        <button
          onClick={handleClose}
          className="absolute right-4 top-4 rounded-full p-2 hover:bg-white/10 transition-colors z-10"
          aria-label="Fechar"
        >
          <X className="h-5 w-5 text-white/70 hover:text-white" />
        </button>

        <DialogHeader className="space-y-4 pt-6">
          {/* Ícones ilustrativos */}
          <div className="flex justify-center items-center gap-4 mb-2">
            <div className="p-3 rounded-full bg-white/5">
              <Smartphone className="h-8 w-8 text-orange-400" />
            </div>
            <div className="text-white/40 text-2xl">→</div>
            <div className="p-3 rounded-full bg-white/10">
              <Monitor className="h-8 w-8 text-green-400" />
            </div>
          </div>

          {/* Título em destaque */}
          <DialogTitle className="text-2xl font-bold text-center text-white leading-tight">
            Acesse no Computador para a Experiência Completa
          </DialogTitle>

          {/* Descrição */}
          <DialogDescription className="text-center text-white/80 text-base leading-relaxed">
            Esta é uma ferramenta completa e de alta complexidade. No celular/tablet você terá 
            acesso limitado. Para aproveitar 100% das funcionalidades, acesse de um computador.
          </DialogDescription>
        </DialogHeader>

        <DialogFooter className="flex-col gap-2 sm:flex-col pt-2">
          {/* Botão "Não mostrar novamente" - DESTAQUE */}
          <Button
            onClick={handleDontShowAgain}
            className="w-full bg-white hover:bg-white/90 text-black font-semibold py-6 text-base shadow-lg"
          >
            Não mostrar novamente
          </Button>

          {/* Botão secundário "Continuar no Mobile" */}
          <Button
            onClick={handleClose}
            variant="ghost"
            className="w-full text-white/70 hover:text-white hover:bg-white/5 py-6"
          >
            Continuar no Mobile
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
